import { useEffect } from 'react'
import { Wallet, TrendingUp, TrendingDown, DollarSign } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateIncomeType, updateCategory, updateTaxSettings, updateRetirementContribution } from '@/store/slices/budgetSlice'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import { US_STATES, type FilingStatus } from '@/types'

export default function Budget() {
  const dispatch = useAppDispatch()
  const budget = useAppSelector(state => state.budget)
  const { calculations, incomeTypes, categories, taxSettings, retirementContributions } = budget

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('budget'))
  }, [dispatch])

  const handleIncomeChange = (type: string, amount: number) => {
    dispatch(updateIncomeType({ type, amount }))
  }

  const handleExpenseChange = (key: string, amount: number) => {
    dispatch(updateCategory({ key, updates: { amount } }))
  }

  const handleTaxSettingChange = (setting: keyof typeof taxSettings, value: unknown) => {
    dispatch(updateTaxSettings({ setting, value }))
  }

  const handleRetirementContributionChange = (type: keyof typeof retirementContributions, amount: number) => {
    dispatch(updateRetirementContribution({ type, amount }))
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Budget Manager" 
        description="Track income, expenses, and taxes."
      />

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Gross Income"
          value={formatCurrency(calculations.grossIncome)}
          icon={DollarSign}
        />
        <StatCard
          title="Net Income"
          value={formatCurrency(calculations.netIncome)}
          icon={TrendingUp}
          changeType={calculations.netIncome > 0 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Total Expenses"
          value={formatCurrency(calculations.totalExpenses)}
          icon={TrendingDown}
        />
        <StatCard
          title="Free Cash Flow"
          value={formatCurrency(calculations.freeCashFlow)}
          icon={Wallet}
          changeType={calculations.freeCashFlow > 0 ? 'positive' : 'negative'}
          change={calculations.freeCashFlow > 0 ? 'Surplus' : 'Deficit'}
        />
      </div>

      <Tabs defaultValue="income" className="space-y-4">
        <TabsList>
          <TabsTrigger value="income">Income</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="taxes">Taxes</TabsTrigger>
          <TabsTrigger value="retirement">Retirement</TabsTrigger>
        </TabsList>

        {/* Income Tab */}
        <TabsContent value="income">
          <Card>
            <CardHeader>
              <CardTitle>Income Sources</CardTitle>
              <CardDescription>Enter your annual income from all sources</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(incomeTypes).map(([key, income]) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{income.name} (Annual)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id={key}
                        type="number"
                        className="pl-9"
                        value={income.amount || ''}
                        onChange={(e) => handleIncomeChange(key, parseFloat(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Gross Income</span>
                  <span className="text-2xl font-bold text-success">
                    {formatCurrency(calculations.grossIncome)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Expenses</CardTitle>
              <CardDescription>Enter your monthly expenses by category</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(categories)
                  .filter(([, cat]) => cat.type === 'expense' && !cat.autoCalculated)
                  .map(([key, category]) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key}>{category.name}</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id={key}
                          type="number"
                          className="pl-9"
                          value={category.amount || ''}
                          onChange={(e) => handleExpenseChange(key, parseFloat(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  ))}
              </div>
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Annual Expenses</span>
                  <span className="text-2xl font-bold text-destructive">
                    {formatCurrency(calculations.totalExpenses)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Taxes Tab */}
        <TabsContent value="taxes">
          <Card>
            <CardHeader>
              <CardTitle>Tax Settings</CardTitle>
              <CardDescription>Configure your tax information for accurate calculations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Filing Status</Label>
                  <Select
                    value={taxSettings.filingStatus}
                    onValueChange={(value: FilingStatus) => handleTaxSettingChange('filingStatus', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married Filing Jointly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={taxSettings.userState}
                    onValueChange={(value) => handleTaxSettingChange('userState', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 pt-4 border-t">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Federal Tax</p>
                  <p className="text-xl font-bold text-destructive">{formatCurrency(calculations.federalTax)}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">State Tax</p>
                  <p className="text-xl font-bold text-destructive">{formatCurrency(calculations.stateTax)}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Total Tax</p>
                  <p className="text-xl font-bold text-destructive">{formatCurrency(calculations.totalTax)}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Effective Rate</p>
                  <p className="text-xl font-bold">
                    {calculations.grossIncome > 0 
                      ? formatPercentage((calculations.totalTax / calculations.grossIncome) * 100)
                      : '0%'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retirement Tab */}
        <TabsContent value="retirement">
          <Card>
            <CardHeader>
              <CardTitle>Retirement Contributions</CardTitle>
              <CardDescription>Annual retirement account contributions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>401(k) Pre-Tax</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-9"
                      value={retirementContributions.preTax401k.amount || ''}
                      onChange={(e) => handleRetirementContributionChange('preTax401k', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max: ${retirementContributions.preTax401k.max.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Roth 401(k)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-9"
                      value={retirementContributions.roth401k.amount || ''}
                      onChange={(e) => handleRetirementContributionChange('roth401k', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max: ${retirementContributions.roth401k.max.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Traditional IRA</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-9"
                      value={retirementContributions.traditionalIRA.amount || ''}
                      onChange={(e) => handleRetirementContributionChange('traditionalIRA', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max: ${retirementContributions.traditionalIRA.max.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Roth IRA</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-9"
                      value={retirementContributions.rothIRA.amount || ''}
                      onChange={(e) => handleRetirementContributionChange('rothIRA', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max: ${retirementContributions.rothIRA.max.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>HSA</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      className="pl-9"
                      value={retirementContributions.hsa.amount || ''}
                      onChange={(e) => handleRetirementContributionChange('hsa', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Max: ${retirementContributions.hsa.max.toLocaleString()}</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Retirement Contributions</span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCurrency(
                      Object.values(retirementContributions).reduce((sum, c) => sum + c.amount, 0)
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
